import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import CommonModal from '@/components/common/CommonModal';
import FormField from '@/components/common/FormField';
import Button from '@/components/common/Button';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification, generateSlug } from '@/utility/helper';
import { errorTypes } from '@/utility/validation';
import { IAddTagForm, ITag } from '@/interfaces/Tag/tag.interface';
import { CommonSelect } from '@/components/common/CommonSelect';

interface AddTagProps {
    showAddModal: boolean;
    setShowAddModal: (show: boolean) => void;
    modalMode: 'create' | 'edit';
    selectedTag?: ITag | null;
    onSuccess?: () => void;
}

export const AddTag: React.FC<AddTagProps> = ({
    showAddModal,
    setShowAddModal,
    modalMode,
    selectedTag,
    onSuccess,
}) => {
    const {
        register,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors, isSubmitting },
    } = useForm<IAddTagForm>();


    useEffect(() => {
        if (showAddModal) {
            if (modalMode === 'edit' && selectedTag) {
                reset({
                    name: selectedTag.name,
                    slug: selectedTag.slug
                });
            } else {
                reset({
                    name: '',
                    slug: '',
                });
            }
        }
    }, [showAddModal, modalMode, selectedTag, reset]);

    const onSubmit = async (data: IAddTagForm) => {
        try {
            const url = modalMode === 'edit' && selectedTag
                ? `${API_URL + API_ENDPOINT.tags}/${selectedTag.id}`
                : `${API_URL + API_ENDPOINT.tags}`;

            const method = modalMode === 'edit' ? 'PATCH' : 'POST';

            const response = await callApi<any>(
                url,
                {
                    method: method,
                    data: data,
                },
            );

            showToastNotification('success', response?.message || `Tag ${modalMode === 'edit' ? 'updated' : 'created'} successfully`);
            setShowAddModal(false);
            onSuccess?.();
        } catch (error) {
            ThrowError(error);
        }
    };

    return (
        <CommonModal
            open={showAddModal}
            onOpenChange={setShowAddModal}
            title={modalMode === 'edit' ? "Edit Tag" : "Create New Tag"}
        >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                    label="Tag Name"
                    name="name"
                    requiredIcon
                    placeholder="e.g. Best Seller"
                    error={errors.name?.message}
                    {...register('name', {
                        ...errorTypes.required('Tag Name'),
                        onChange: (e) => {
                            if (modalMode === 'create') {
                                setValue('slug', generateSlug(e.target.value));
                            }
                        }
                    })}
                />


                <div className="flex justify-end gap-3 pt-6 border-t border-border">
                    <button
                        type="button"
                        onClick={() => setShowAddModal(false)}
                        className="h-9 px-4 rounded-lg border text-sm font-bold hover:bg-secondary transition-colors"
                    >
                        Cancel
                    </button>
                    <Button type="submit" isLoading={isSubmitting}>
                        {modalMode === 'edit' ? "Update Tag" : "Create Tag"}
                    </Button>
                </div>
            </form>
        </CommonModal>
    );
};
